/**
 * Typography Theme Configuration
 *
 * Defines all typography variants, sizes, and styles using MUI's theme system
 * This centralizes all typography styling and improves performance by computing styles once
 */

import { Components, Theme } from "@mui/material/styles";
import { SemanticTokens } from "../tokens/semantic";

// ═══════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════

/**
 * Canonical type scale — use these for all new code.
 *
 * Five groups, 31 variants. A variant here carries ONLY
 * font-size/weight/family (plus tabular-nums for prices). It never encodes
 * color or layout:
 *   - color goes through the standard MUI `color` prop, e.g. `color="error"`,
 *     `color="textSecondary"` (camelCase — NOT `"text.secondary"`, which
 *     silently no-ops since MUI's `color` prop only recognizes the fixed
 *     keywords below, unlike `sx` which does resolve dotted palette
 *     paths). Already wired to the semantic tokens via the palette in
 *     themeConfig.ts: "primary" | "secondary" | "success" | "error" |
 *     "info" | "warning" | "textPrimary" | "textSecondary" |
 *     "textDisabled". For a semantic token with no palette slot, use
 *     `sx={{ color: theme.ameen.colors... }}` (dotted paths are fine
 *     inside sx).
 *   - alignment/margin/width/position/nowrap/etc. go through `sx` at the
 *     call site — those are per-usage layout concerns, not shared type
 *     scale.
 *
 * Prefer the <Text> wrapper (src/components/Text) over raw MUI
 * <Typography>, mirroring how <Button> wraps MUI's Button.
 *
 * Naming notes:
 *   - Hierarchy uses UPPERCASE H1–H6 on purpose: lowercase `h1`–`h6` are
 *     MUI's own built-in variants (~300 call sites), so reusing those keys
 *     would silently restyle the whole app. `H1` maps to the <h1> tag via
 *     `variantMapping` below without touching MUI defaults.
 *   - Display's 16px row is `displayMd` (not `display`): `display` is a
 *     deprecated alias below (old 36px hero) and keys must be unique.
 *   - `caption` spec'd as 12px/0.875rem is a spec typo (0.875rem = 14px);
 *     it is implemented as 12px/0.75rem. `buttonLink` carries no underline —
 *     add `sx={{ textDecoration: 'underline' }}` at the call site if needed.
 *   - REDEFINED keys (same name, new metrics — see migration table on
 *     DeprecatedAmeenTypographyAlias): `bodyLg` was 16px/500, now
 *     18px/400; `label` was 12px/400, now 14px/400.
 */
export type AmeenTypographyVariant =
  // ── Hierarchy (unsuffixed = SemiBold 600, Medium suffix = 500) ──
  | "H1" // 40px / 2.5rem, 600
  | "H1Medium" // 40px / 2.5rem, 500
  | "H2" // 36px / 2.25rem, 600
  | "H2Medium" // 36px / 2.25rem, 500
  | "H3" // 32px / 2rem, 600
  | "H3Medium" // 32px / 2rem, 500
  | "H4" // 28px / 1.75rem, 600
  | "H4Medium" // 28px / 1.75rem, 500
  | "H5" // 24px / 1.5rem, 600
  | "H5Medium" // 24px / 1.5rem, 500
  | "H6" // 20px / 1.25rem, 600
  | "H6Medium" // 20px / 1.25rem, 500
  // ── Display ──
  | "displayPrice" // 18px / 1.125rem, 600, tabular-nums
  | "displayPriceMedium" // 18px / 1.125rem, 500, tabular-nums
  | "displayLg" // 18px / 1.125rem, 600
  | "displayLgMedium" // 18px / 1.125rem, 500
  | "displayMd" // 16px / 1rem, 500
  | "displayMdStrong" // 16px / 1rem, 600
  | "displaySm" // 14px / 0.875rem, 500
  | "displaySmRegular" // 14px / 0.875rem, 400
  | "displayXs" // 12px / 0.75rem, 600 only
  // ── Body (all Regular 400) ──
  | "bodyLg" // 18px / 1.125rem — REDEFINED, was 16px/500
  | "bodyBase" // 16px / 1rem
  | "bodySm" // 14px / 0.875rem
  | "bodyXs" // 12px / 0.75rem
  // ── Buttons ──
  | "buttonBase" // 16px / 1rem, 500
  | "buttonSm" // 14px / 0.875rem, 500
  | "buttonXs" // 12px / 0.75rem, 400 (per spec)
  | "buttonLink" // 14px / 0.875rem, 500
  // ── Labels ──
  | "label" // 14px / 0.875rem, 400 — REDEFINED, was 12px/400
  | "caption" // 12px / 0.75rem, 400
  // ── Deprecated aliases (previous canonical scale, kept so existing
  // <Text> call sites keep compiling; do not use in new code) ──
  | DeprecatedAmeenTypographyAlias;

/**
 * Previous canonical scale — superseded by the H/Display/Body/Button/Label
 * groups above. Each member is kept with its ORIGINAL px metrics so existing
 * screens render byte-identical while migration happens file by file.
 *
 * Closest-new-variant migration map (size-exact unless noted):
 *   display (36/600) → H2 · displaySmall (32/500) → H3Medium ·
 *   displaySmallStrong (32/600) → H3 · title (24/500) → H5Medium ·
 *   titleRegular (24/400) → H5Medium (approx, weight 400→500) ·
 *   titleLight (24/300) → H5Medium (approx) · titleStrong (24/600) → H5 ·
 *   subtitle (20/500) → H6Medium · subtitleStrong (20/600) → H6 ·
 *   bodyXl (18/500) → displayLgMedium · bodyXlRegular (18/400) → bodyLg ·
 *   bodyXlStrong (18/600) → displayLg · bodyLgRegular (16/400) → bodyBase ·
 *   bodyLgStrong (16/600) → displayMdStrong ·
 *   bodyLgLight (16/300) → bodyBase (approx) · bodyMd (14/500) → displaySm ·
 *   bodyMdRegular (14/400) → bodySm ·
 *   bodyMdStrong (14/600) → displaySm (approx, weight 600→500) ·
 *   labelStrong (12/500) → displayXs (approx, weight 500→600).
 *
 * Two old names are NOT listed here because the new scale reuses their keys
 * with new metrics: old `bodyLg` (16/500, 21 usages) → use `displayMd`
 * for the identical look; old `label` (12/400, 10 usages) → use `caption`
 * (or `bodyXs`) for the identical look.
 *
 * @deprecated Migrate call sites to AmeenTypographyVariant + color prop + sx.
 */
export type DeprecatedAmeenTypographyAlias =
  | "display"
  | "displaySmall"
  | "displaySmallStrong"
  | "title"
  | "titleRegular"
  | "titleLight"
  | "titleStrong"
  | "subtitle"
  | "subtitleStrong"
  | "bodyXl"
  | "bodyXlRegular"
  | "bodyXlStrong"
  | "bodyLgRegular"
  | "bodyLgStrong"
  | "bodyLgLight"
  | "bodyMd"
  | "bodyMdRegular"
  | "bodyMdStrong"
  | "labelStrong";

/**
 * Legacy variants — inventoried, not to be extended. Each of these bakes
 * color and often layout into the name (that's the anti-pattern being
 * replaced). They stay defined, unchanged, so existing call sites keep
 * rendering exactly as before while migration to AmeenTypographyVariant
 * happens file by file. Do not add new names here — add to the
 * canonical scale above instead and move color/layout to the call site.
 *
 * @deprecated Migrate call sites to AmeenTypographyVariant + color prop + sx.
 */
export type LegacyAmeenTypographyVariant =
  // ═══════════════════════════════════════════════════════════
  // COMMON TYPOGRAPHY VARIANTS (New - replacing inline sx)
  // ═══════════════════════════════════════════════════════════
  // Page titles
  | "pageTitle36" // 36px, fontWeight 600
  | "pageTitle" // 24px, fontWeight 500
  | "pageTitleLight" // 24px, fontWeight 300
  | "pageTitleGreen" // 24px, color green
  | "pageTitleRed" // 24px, color red
  | "pageTitleBold"
  // Section titles
  | "sectionTitle" // 20px, fontWeight 600
  | "pricePrimaryDarkTitle" // 20px, fontWeight 600
  | "sectionTitleMedium" // 20px, fontWeight 500
  // Headers
  | "headerSemibold" // h5 with fontWeight 600
  // Body text sizes
  | "bodyLarge" // 18px
  | "bodyLargeMedium" // 18px, fontWeight 500
  | "bodyLargeRegular" // 18px, fontWeight 400
  | "bodyMedium" // 16px
  | "bodyMediumBold"
  | "bodyMediumRegular" // 16px, fontWeight 400
  | "bodyMediumLight" // 16px, fontWeight 300
  | "bodySmall" // 14px
  | "bodySmallRegular" // 14px, fontWeight 400
  | "bodyMiddleRegular" // 14px, fontWeight 500
  | "caption" // 12px
  | "captionBold" // 12px, fontWeight 500
  | "captionSecondary" // 12px, color secondary
  // Color variants
  | "textPrimary" // color: text.primary
  | "textSecondary" // color: text.secondary
  | "textTertiary" // color: text.tertiary
  | "textSuccess" // color: success
  | "textError" // color: error
  | "textInfo" // color: info
  // Numbers variants
  | "amnShowNumbersBase"
  | "amnShowNumbersRed"
  | "amnShowNumbersBase500"
  | "amnShowNumbers500Red"
  | "amnShowNumbersBase600"
  | "amnShowNumbers600Red"
  | "amnShowNumbers16Gray"
  | "amnShowNumbers16Red"
  | "amnShowNumbers14GraybgSilveSand"
  | "amnShowNumbers14RedbgSilveSand"
  | "amnShowNumbers16500DarkBlueAlignRightSelfCenter"
  | "amnShowNumbers16500RedAlignRightSelfCenter"
  | "amnShowNumbers16mr19ml2Base"
  | "amnShowNumbers16mr19ml2Red"
  | "amnShowNumbers18400"
  | "amnShowNumbers18Red"
  | "amnShowNumbers18400Green"
  | "amnShowNumbers18400Primary"
  | "amnShowNumbers18pr1Primary"
  | "amnShowNumbers18pr1Red"
  | "amnShowNumbers18mr8p5pr1Primary"
  | "amnShowNumbers18mr8p5pr1Red"
  | "amnShowNumbers20500Base"
  | "amnShowNumbers20500Red"
  | "amnShowNumbers20500Primary"
  | "amnShowNumbers24400Green"
  | "amnShowNumbers24400Red"
  | "amnShowNumberswidth100py05px3Base"
  | "amnShowNumberswidth100py05px3RedbgPink"
  // 32px Directional variants
  | "amnShow32500DirectionalRed"
  | "amnShow32500DirectionalBase"
  | "amnShow32600CancelledRed"
  | "amnShow32600TransactionsBase"
  | "amnShow32600ExternalGreen"
  | "amnShow32500DirectionalGreen"
  | "amnShow32500DirectionalSecondary"
  | "amnShow18500DirectionalHardBlue"
  | "amnShow32500DirectionalGrayBlued"
  // Right alignment variants
  | "amnShowRightAlignement"
  | "amnShowRightAlignementGreen"
  | "amnShowRightAlignementOffWhite"
  | "amnShowRightAlignementRed"
  | "amnShowRightAlignementGray"
  | "amnShowRightAlignementGreenBackGroundGreen"
  | "amnShowRightAlignementRedBackGroundRed"
  | "amnShowRightAlignemenGrey16"
  | "amnShowRightAlignemenRed16"
  | "amnShowRightAlignement16600Base"
  | "amnShowRightAlignement16600Red"
  | "amnShowRightAlignement20600Base"
  | "amnShowRightAlignement20600Red"
  | "amnShowRightAlignementAlwaysRight"
  | "amnShowRightAlignementAlwaysRightRed"
  // Center alignment variants
  | "amnShowcenterBlack34600"
  | "amnShowcenterRed34600"
  | "amnShowcenterBlack16mr20"
  | "amnShowcenterRed16mr20"
  | "amnShowcenterWidth40FlexCenter"
  | "amnShowcenterWidth40FlexCenterRed"
  | "amnShowcenterWidth30FlexEnd"
  | "amnShowcenterWidth30FlexEndRed"
  | "amnShowcenterWidth50FlexEnd"
  | "amnShowcenterWidth50FlexEndRed"
  | "amnShowcenterWidthautoFlexCenter"
  | "amnShowcenterWidthautoFlexCenterRed"
  // Margin nowrap variants
  | "amnShowmry2mrx3nowrap18"
  | "amnShowmry2mrx3nowrap18Red"
  // 16px 400 variants
  | "amnShow16400Base"
  | "amnShow16400Red"
  // 18px variants
  | "amnShow18500"
  | "amnShow18500Red"
  | "amnShow18600Green"
  | "amnShow18600Red"
  // 12px width variants
  | "amnShow12width50alignBase"
  | "amnShow12width50alignRed"
  | "amnShow12width50alignGreen"
  // End alignment variants
  | "amnShowEnd18Primary"
  | "amnShowEnd18Red"
  // Span variants
  | "amnShowSpan14GreenDark"
  | "amnShowSpan14Red"
  | "amnShowSpan14Base"
  // Flex variants
  | "amnShowFlex1br4br1p1Green"
  | "amnShowFlex1br4br1p1Red"
  | "amnShowFlex1br4br1p1Base"
  // Indicator variants
  | "indicatorTopInfoOffsetCyan"
  | "indicatorTopOffsetOrange"
  | "indicatorInfoTopRightBlue"
  | "indicatorTopZeroRightInfoOffsetCyan"
  // Scaled variants
  | "smallUp"
  | "smallDown"
  | "smallEqual"
  | "mediumUp"
  | "mediumDown"
  | "mediumEqual"
  | "largeUp"
  | "largeDown"
  | "largeEqual"
  | "InfoOffsetCyan"
  | "InfoOffsetCyanOrange";

/**
 * Everything MUI's `variant` prop must accept at the type level: the
 * canonical scale (including its deprecated aliases) plus every still-live
 * legacy name. Used only for the MUI module augmentation (see
 * muiThemeOverrides.d.ts) — application code should reference the
 * non-deprecated members of AmeenTypographyVariant (via <Text>) directly
 * instead.
 */
export type AllAmeenTypographyVariant =
  | AmeenTypographyVariant
  | LegacyAmeenTypographyVariant;

// ═══════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Helper function to create typography variants with color variations
 */
const createColorVariants = (
  baseName: string,
  baseStyle: any,
  colorVariations: { suffix: string; color: string }[]
) => {
  return colorVariations.map(({ suffix, color }) => ({
    props: { variant: `${baseName}${suffix}` as any },
    style: {
      fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
      ...baseStyle,
      ...(color && { color }),
    },
  }));
};

/**
 * Helper for directional text alignment based on language
 */
const getDirectionalAlign = (
  type: "right" | "end" | "start",
  isRtl: boolean
) => {
  if (type === "right") return isRtl ? "left" : "right";
  if (type === "end") return isRtl ? "start" : "end";
  return isRtl ? "end" : "start";
};

// ═══════════════════════════════════════════════════════════
// MAIN TYPOGRAPHY THEME CREATOR
// ═══════════════════════════════════════════════════════════

export interface TypographyThemeConfig {
  colors: SemanticTokens;
  isRtl: boolean;
}

/**
 * Creates the MuiTypography theme configuration with all custom variants
 */
export const createTypographyTheme = (
  config: TypographyThemeConfig
): Components<Theme>["MuiTypography"] => {
  const { colors, isRtl } = config;

  // Color mapping for variants
  const colorMap = {
    Primary: colors.brand.primary,
    Secondary: colors.brand.secondary,
    Red: colors.status.error,
    Green: colors.status.success,
    SuccessGreen: colors.status.success,
    GreenDark: colors.text.success,
    PaidGreen: colors.status.success,
    HardBlack: colors.text.primary,
    GrayBlued: colors.text.secondary,
    SilverSand: colors.background.tertiary,
    OffWhite: colors.background.tertiary,
    DarkBlue: colors.brand.primaryActive,
    HardBlue: colors.brand.primary,
  };

  // Canonical scale: font-size/weight/family only — no color, no layout.
  // rem units so type scales with the root font-size (16px default:
  // 2.5rem = 40px, 2.25rem = 36px, 2rem = 32px, 1.75rem = 28px,
  // 1.5rem = 24px, 1.25rem = 20px, 1.125rem = 18px, 1rem = 16px,
  // 0.875rem = 14px, 0.75rem = 12px). Price variants add tabular-nums so
  // totals/amounts align digit-for-digit.
  const scale = (
    fontSize: string,
    fontWeight: number,
    extra?: Record<string, unknown>
  ) => ({
    fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
    fontSize,
    fontWeight,
    ...extra,
  });
  const tabularNums = { fontVariantNumeric: "tabular-nums" };

  return {
    variants: [
      // ═══════════════════════════════════════════════════════════
      // CANONICAL TYPE SCALE (use these for all new code — see
      // AmeenTypographyVariant doc-comment above for the color/layout
      // convention that goes with it)
      // ═══════════════════════════════════════════════════════════
      // ── Hierarchy: H1–H6 (SemiBold default, Medium sub-variant) ──
      { props: { variant: "H1" as any }, style: scale("2.5rem", 600) },
      { props: { variant: "H1Medium" as any }, style: scale("2.5rem", 500) },
      { props: { variant: "H2" as any }, style: scale("2.25rem", 600) },
      { props: { variant: "H2Medium" as any }, style: scale("2.25rem", 500) },
      { props: { variant: "H3" as any }, style: scale("2rem", 600) },
      { props: { variant: "H3Medium" as any }, style: scale("2rem", 500) },
      { props: { variant: "H4" as any }, style: scale("1.75rem", 600) },
      { props: { variant: "H4Medium" as any }, style: scale("1.75rem", 500) },
      { props: { variant: "H5" as any }, style: scale("1.5rem", 600) },
      { props: { variant: "H5Medium" as any }, style: scale("1.5rem", 500) },
      { props: { variant: "H6" as any }, style: scale("1.25rem", 600) },
      { props: { variant: "H6Medium" as any }, style: scale("1.25rem", 500) },
      // ── Display ──
      {
        props: { variant: "displayPrice" as any },
        style: scale("1.125rem", 600, tabularNums),
      },
      {
        props: { variant: "displayPriceMedium" as any },
        style: scale("1.125rem", 500, tabularNums),
      },
      { props: { variant: "displayLg" as any }, style: scale("1.125rem", 600) },
      {
        props: { variant: "displayLgMedium" as any },
        style: scale("1.125rem", 500),
      },
      { props: { variant: "displayMd" as any }, style: scale("1rem", 500) },
      {
        props: { variant: "displayMdStrong" as any },
        style: scale("1rem", 600),
      },
      { props: { variant: "displaySm" as any }, style: scale("0.875rem", 500) },
      {
        props: { variant: "displaySmRegular" as any },
        style: scale("0.875rem", 400),
      },
      { props: { variant: "displayXs" as any }, style: scale("0.75rem", 600) },
      // ── Body (all Regular 400) ──
      { props: { variant: "bodyLg" as any }, style: scale("1.125rem", 400) },
      { props: { variant: "bodyBase" as any }, style: scale("1rem", 400) },
      { props: { variant: "bodySm" as any }, style: scale("0.875rem", 400) },
      { props: { variant: "bodyXs" as any }, style: scale("0.75rem", 400) },
      // ── Buttons ──
      { props: { variant: "buttonBase" as any }, style: scale("1rem", 500) },
      {
        props: { variant: "buttonSm" as any },
        style: scale("0.875rem", 500),
      },
      { props: { variant: "buttonXs" as any }, style: scale("0.75rem", 400) },
      {
        props: { variant: "buttonLink" as any },
        style: scale("0.875rem", 500),
      },
      // ── Labels ──
      { props: { variant: "label" as any }, style: scale("0.875rem", 400) },
      { props: { variant: "caption" as any }, style: scale("0.75rem", 400) },

      // ═══════════════════════════════════════════════════════════
      // DEPRECATED ALIASES — previous canonical scale, kept VERBATIM
      // (original px metrics) so existing call sites render byte-identical
      // while migrating per DeprecatedAmeenTypographyAlias. Do not add to
      // this section; extend the canonical scale above.
      // NOTE: old `bodyLg` (16px/500) and old `label` (12px/400) are NOT
      // here — their keys were reused by the new scale (bodyLg = 18px/400,
      // label = 14px/400). For the identical old look use `displayMd`
      // instead of old bodyLg, and `caption`/`bodyXs` instead of old label.
      // ═══════════════════════════════════════════════════════════
      { props: { variant: "display" as any }, style: scale("36px", 600) },
      { props: { variant: "displaySmall" as any }, style: scale("32px", 500) },
      {
        props: { variant: "displaySmallStrong" as any },
        style: scale("32px", 600),
      },
      { props: { variant: "title" as any }, style: scale("24px", 500) },
      { props: { variant: "titleRegular" as any }, style: scale("24px", 400) },
      { props: { variant: "titleLight" as any }, style: scale("24px", 300) },
      { props: { variant: "titleStrong" as any }, style: scale("24px", 600) },
      { props: { variant: "subtitle" as any }, style: scale("20px", 500) },
      {
        props: { variant: "subtitleStrong" as any },
        style: scale("20px", 600),
      },
      { props: { variant: "bodyXl" as any }, style: scale("18px", 500) },
      { props: { variant: "bodyXlRegular" as any }, style: scale("18px", 400) },
      { props: { variant: "bodyXlStrong" as any }, style: scale("18px", 600) },
      { props: { variant: "bodyLgRegular" as any }, style: scale("16px", 400) },
      { props: { variant: "bodyLgStrong" as any }, style: scale("16px", 600) },
      { props: { variant: "bodyLgLight" as any }, style: scale("16px", 300) },
      { props: { variant: "bodyMd" as any }, style: scale("14px", 500) },
      { props: { variant: "bodyMdRegular" as any }, style: scale("14px", 400) },
      { props: { variant: "bodyMdStrong" as any }, style: scale("14px", 600) },
      { props: { variant: "labelStrong" as any }, style: scale("12px", 500) },

      // ═══════════════════════════════════════════════════════════
      // LEGACY — deprecated, kept verbatim for existing call sites.
      // Do not add to this section; extend the canonical scale above.
      // ═══════════════════════════════════════════════════════════
      // ═══════════════════════════════════════════════════════════
      // COMMON TYPOGRAPHY VARIANTS (New - replacing inline sx)
      // ═══════════════════════════════════════════════════════════

      // Page titles (24px)
      {
        props: { variant: "pageTitle" as any },
        style: {
          fontSize: "24px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pageTitle36" as any },
        style: {
          fontSize: "36px",
          fontWeight: 600,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pageTitleBold" as any },
        style: {
          fontSize: "24px",
          fontWeight: 600,
          color: colors.status.successActive,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pageTitleLight" as any },
        style: {
          fontSize: "24px",
          fontWeight: 300,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pageTitleGreen" as any },
        style: {
          fontSize: "24px",
          fontWeight: 600,
          color: colors.status.success,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pageTitleRed" as any },
        style: {
          fontSize: "24px",
          fontWeight: 600,
          color: colors.status.error,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },

      // Section titles (20px)
      {
        props: { variant: "sectionTitle" as any },
        style: {
          fontSize: "20px",
          fontWeight: 600,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "pricePrimaryDarkTitle" as any },
        style: {
          fontSize: "20px",
          fontWeight: 600,
          color: colors.brand.primaryActive,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "sectionTitleMedium" as any },
        style: {
          fontSize: "20px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },

      // Headers
      {
        props: { variant: "headerSemibold" as any },
        style: {
          fontWeight: 600,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },

      // Body text sizes
      {
        props: { variant: "bodyLarge" as any },
        style: {
          fontSize: "18px",
          fontWeight: 600,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyLargeMedium" as any },
        style: {
          fontSize: "18px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyLargeRegular" as any },
        style: {
          fontSize: "18px",
          fontWeight: 400,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyMedium" as any },
        style: {
          fontSize: "16px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyMediumBold" as any },
        style: {
          fontSize: "16px",
          fontWeight: 600,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyMediumRegular" as any },
        style: {
          fontSize: "16px",
          fontWeight: 400,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyMediumLight" as any },
        style: {
          fontSize: "16px",
          fontWeight: 300,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodySmall" as any },
        style: {
          fontSize: "14px",
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodySmallRegular" as any },
        style: {
          fontSize: "14px",
          fontWeight: 400,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "bodyMiddleRegular" as any },
        style: {
          fontSize: "14px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "caption" as any },
        style: {
          // Same as canonical `caption` (12px / 0.75rem, 400) — legacy raw
          // <Typography variant="caption"> call sites keep their look.
          fontSize: "0.75rem",
          fontWeight: 400,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "captionBold" as any },
        style: {
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },
      {
        props: { variant: "captionSecondary" as any },
        style: {
          fontSize: "12px",
          color: colors.text.secondary,
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
        },
      },

      // Color variants
      {
        props: { variant: "textPrimary" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.text.primary,
        },
      },
      {
        props: { variant: "textSecondary" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.text.secondary,
        },
      },
      {
        props: { variant: "textTertiary" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.text.tertiary,
        },
      },
      {
        props: { variant: "textSuccess" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.status.success,
        },
      },
      {
        props: { variant: "textError" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.status.error,
        },
      },
      {
        props: { variant: "textInfo" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          color: colors.status.info,
        },
      },

      // ═══════════════════════════════════════════════════════════
      // NUMBERS VARIANTS
      // ═══════════════════════════════════════════════════════════

      // Base number variants (16px)
      ...createColorVariants("amnShowNumbers", { fontSize: "16px" }, [
        { suffix: "Base", color: colorMap.HardBlack },
        { suffix: "Red", color: colorMap.Red },
      ]),

      // 16px with weights
      ...createColorVariants(
        "amnShowNumbersBase",
        { fontSize: "16px", fontWeight: 500 },
        [{ suffix: "500", color: colorMap.HardBlack }]
      ),
      {
        props: { variant: "amnShowNumbers500Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Red,
          fontWeight: 500,
        },
      },

      ...createColorVariants(
        "amnShowNumbersBase",
        { fontSize: "16px", fontWeight: 600 },
        [{ suffix: "600", color: colorMap.HardBlack }]
      ),
      {
        props: { variant: "amnShowNumbers600Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Red,
          fontWeight: 600,
        },
      },

      // 16px variants
      ...createColorVariants("amnShowNumbers16", { fontSize: "16px" }, [
        { suffix: "Gray", color: colorMap.GrayBlued },
        { suffix: "Red", color: colorMap.Red },
      ]),

      // 14px with backgrounds
      ...createColorVariants(
        "amnShowNumbers14",
        { fontSize: "14px", background: colorMap.SilverSand },
        [
          { suffix: "GraybgSilveSand", color: colorMap.GrayBlued },
          { suffix: "RedbgSilveSand", color: colorMap.Red },
        ]
      ),

      // Align right self center variants
      {
        props: {
          variant: "amnShowNumbers16500DarkBlueAlignRightSelfCenter" as any,
        },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.DarkBlue,
          minWidth: 80,
          textAlign: "right",
          alignSelf: "center",
        },
      },
      {
        props: { variant: "amnShowNumbers16500RedAlignRightSelfCenter" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          minWidth: 80,
          textAlign: "right",
          alignSelf: "center",
          color: colorMap.Red,
        },
      },

      // 16px with margins
      ...createColorVariants(
        "amnShowNumbers16mr19ml2",
        { fontSize: "16px", mr: "19px", ml: 2 },
        [
          { suffix: "Base", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // 16px 400 variants
      ...createColorVariants(
        "amnShow16400",
        { fontSize: "16px", fontWeight: 400 },
        [
          { suffix: "Base", color: colorMap.HardBlack },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // 18px variants
      {
        props: { variant: "amnShowNumbers18400" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
        },
      },
      ...createColorVariants("amnShowNumbers18400", { fontSize: "18px" }, [
        { suffix: "Green", color: colorMap.SuccessGreen },
        { suffix: "Primary", color: colorMap.Primary },
      ]),
      {
        props: { variant: "amnShowNumbers18Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
          color: colorMap.Red,
        },
      },

      // 18px with padding
      ...createColorVariants(
        "amnShowNumbers18pr1",
        { pr: 1, fontSize: "18px" },
        [
          { suffix: "Primary", color: colorMap.Primary },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      ...createColorVariants(
        "amnShowNumbers18mr8p5pr1",
        { pr: 1, fontSize: "18px", mr: 8.5 },
        [
          { suffix: "Primary", color: colorMap.Primary },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // 20px variants
      ...createColorVariants(
        "amnShowNumbers20500",
        { fontSize: "20px", fontWeight: 500 },
        [
          { suffix: "Primary", color: colorMap.Primary },
          { suffix: "Base", color: "" },
        ]
      ),
      {
        props: { variant: "amnShowNumbers20500Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          pr: 1,
          fontSize: "20px",
          fontWeight: 500,
          color: colorMap.Red,
        },
      },

      // 24px variants
      ...createColorVariants("amnShowNumbers24400", { fontSize: "24px" }, [
        { suffix: "Green", color: colorMap.SuccessGreen },
        { suffix: "Red", color: colorMap.Red },
      ]),

      // Width with padding variants
      {
        props: { variant: "amnShowNumberswidth100py05px3Base" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          width: 100,
          borderRadius: 1,
          paddingY: 0.5,
          paddingX: 3,
          direction: isRtl ? "rtl" : "ltr",
          fontSize: "16px",
          color: colorMap.HardBlack,
        },
      },
      {
        props: { variant: "amnShowNumberswidth100py05px3RedbgPink" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          width: 100,
          borderRadius: 1,
          paddingY: 0.5,
          paddingX: 3,
          direction: isRtl ? "rtl" : "ltr",
          color: colorMap.Red,
          background: colors.status.errorBg,
        },
      },

      // ═══════════════════════════════════════════════════════════
      // 32px DIRECTIONAL VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "amnShow32500DirectionalRed" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 500,
          color: colorMap.Red,
        },
      },
      {
        props: { variant: "amnShow32500DirectionalGreen" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 500,
          color: "#02A172",
        },
      },
      {
        props: { variant: "amnShow32600CancelledRed" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 600,
          color: "#C92C2C",
        },
      },
      {
        props: { variant: "amnShow32600ExternalGreen" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 600,
          color: "#006D67",
        },
      },
      {
        props: { variant: "amnShow32500DirectionalSecondary" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          direction: isRtl ? "rtl" : "ltr",
          fontSize: "32px",
          fontWeight: 500,
          color: colorMap.Secondary,
        },
      },
      {
        props: { variant: "amnShow32500DirectionalHardBlue" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          direction: isRtl ? "rtl" : "ltr",
          fontSize: "32px",
          fontWeight: 500,
          color: colorMap.HardBlue,
        },
      },
      {
        props: { variant: "amnShow18500DirectionalHardBlue" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          direction: isRtl ? "rtl" : "ltr",
          fontSize: "18px",
          fontWeight: 500,
          color: colorMap.HardBlue,
        },
      },
      {
        props: { variant: "amnShow32500DirectionalGrayBlued" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          direction: isRtl ? "rtl" : "ltr",
          fontSize: "32px",
          fontWeight: 500,
          color: colorMap.GrayBlued,
        },
      },
      {
        props: { variant: "amnShow32500DirectionalBase" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 500,
          color: colorMap.HardBlack,
        },
      },
      {
        props: { variant: "amnShow32600TransactionsBase" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "32px",
          fontWeight: 600,
          color: colorMap.HardBlack,
        },
      },

      // ═══════════════════════════════════════════════════════════
      // RIGHT ALIGNMENT VARIANTS
      // ═══════════════════════════════════════════════════════════

      // 16px 600 weight
      ...createColorVariants(
        "amnShowRightAlignement16600",
        {
          width: "100%",
          fontSize: "16px",
          fontWeight: 600,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
        [
          { suffix: "Base", color: colorMap.HardBlack },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // 20px 600 weight
      ...createColorVariants(
        "amnShowRightAlignement20600",
        {
          width: "100%",
          fontSize: "20px",
          fontWeight: 600,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
        [
          { suffix: "Base", color: colorMap.HardBlack },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      {
        props: { variant: "amnShowRightAlignement" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          width: "100%",
          fontSize: "14px",
          color: colorMap.HardBlack,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },

      {
        props: { variant: "amnShowRightAlignementDarkBlue" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          width: "100%",
          fontSize: "14px",
          color: colorMap.DarkBlue,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementAlwaysRight" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          width: "100%",
          fontSize: "14px",
          color: colorMap.HardBlack,
          textAlign: "right",
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementGray" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "14px",
          color: colorMap.GrayBlued,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementGreen" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "14px",
          color: colorMap.Green,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementOffWhite" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "14px",
          color: colorMap.OffWhite,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignemenGrey16" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colors.text.secondary,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignemenRed16" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Red,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementRed" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "14px",
          color: colorMap.Red + "!important",
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
        },
      },
      {
        props: { variant: "amnShowRightAlignementAlwaysRightRed" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "14px",
          color: colorMap.Red + "!important",
          textAlign: "right",
          display: "block",
        },
      },

      // With backgrounds
      {
        props: { variant: "amnShowRightAlignementGreenBackGroundGreen" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Green,
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
          background: colors.status.successBg,
          padding: "4px",
          borderRadius: "4px",
          height: "34px",
        },
      },
      {
        props: { variant: "amnShowRightAlignementRedBackGroundRed" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Red + "!important",
          textAlign: getDirectionalAlign("right", isRtl),
          display: "block",
          background: colors.status.errorBg,
          padding: "4px",
          borderRadius: "4px",
          height: "34px",
        },
      },

      // ═══════════════════════════════════════════════════════════
      // CENTER ALIGNMENT VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "amnShowcenterBlack16mr20" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.HardBlack + "!important",
          textAlign: "center",
          display: "block",
          mr: "20px",
        },
      },
      {
        props: { variant: "amnShowcenterRed16mr20" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "16px",
          color: colorMap.Red + "!important",
          textAlign: "center",
          display: "block",
          mr: "20px",
        },
      },
      {
        props: { variant: "amnShowcenterBlack34600" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "34px",
          color: colorMap.HardBlack + "!important",
          textAlign: "center",
          display: "block",
        },
      },
      {
        props: { variant: "amnShowcenterRed34600" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "34px",
          color: colorMap.Red + "!important",
          textAlign: "center",
          display: "block",
        },
      },

      // Width flex variants
      ...createColorVariants(
        "amnShowcenterWidth40FlexCenter",
        { width: "40%", display: "flex", justifyContent: "center" },
        [
          { suffix: "", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      ...createColorVariants(
        "amnShowcenterWidthautoFlexCenter",
        { width: "auto", display: "flex", justifyContent: "center" },
        [
          { suffix: "", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      ...createColorVariants(
        "amnShowcenterWidth30FlexEnd",
        { width: "30%", display: "flex", justifyContent: "flex-end" },
        [
          { suffix: "", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      ...createColorVariants(
        "amnShowcenterWidth50FlexEnd",
        { width: "50%", display: "flex", justifyContent: "flex-end" },
        [
          { suffix: "", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // ═══════════════════════════════════════════════════════════
      // MARGIN NOWRAP VARIANTS
      // ═══════════════════════════════════════════════════════════

      ...createColorVariants(
        "amnShowmry2mrx3nowrap18",
        {
          fontSize: "18px",
          marginLeft: "24px",
          marginRight: "24px",
          marginTop: "16px",
          marginBottom: "16px",
          whiteSpace: "nowrap",
        },
        [
          { suffix: "", color: "" },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // ═══════════════════════════════════════════════════════════
      // 18px VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "amnShow18500" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
          fontWeight: 500,
        },
      },
      {
        props: { variant: "amnShow18500Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
          fontWeight: 500,
          color: colorMap.Red,
        },
      },
      {
        props: { variant: "amnShow18600Green" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "16px",
          color: colorMap.SuccessGreen,
        },
      },
      {
        props: { variant: "amnShow18600Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "16px",
          color: colorMap.Red,
          direction: "ltr",
        },
      },

      // ═══════════════════════════════════════════════════════════
      // 12px WIDTH VARIANTS
      // ═══════════════════════════════════════════════════════════

      ...createColorVariants(
        "amnShow12width50align",
        {
          fontSize: "12px",
          width: "50%",
          textAlign: getDirectionalAlign("end", isRtl),
        },
        [
          { suffix: "Base", color: colorMap.HardBlack },
          { suffix: "Red", color: colorMap.Red },
          { suffix: "Green", color: colorMap.PaidGreen },
        ]
      ),

      // ═══════════════════════════════════════════════════════════
      // END ALIGNMENT VARIANTS
      // ═══════════════════════════════════════════════════════════

      ...createColorVariants(
        "amnShowEnd18",
        { fontSize: "18px", textAlign: "end" },
        [
          { suffix: "Primary", color: colorMap.Primary },
          { suffix: "Red", color: colorMap.Red },
        ]
      ),

      // ═══════════════════════════════════════════════════════════
      // SPAN VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "amnShowSpan14GreenDark" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          component: "span",
          fontSize: "14px",
          color: colorMap.GreenDark,
        },
      },
      {
        props: { variant: "amnShowSpan14Base" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          component: "span",
          fontSize: "14px",
          color: colorMap.HardBlack,
        },
      },
      {
        props: { variant: "amnShowSpan14Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          component: "span",
          fontSize: "14px",
          color: colorMap.Red,
        },
      },

      // ═══════════════════════════════════════════════════════════
      // FLEX VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "amnShowFlex1br4br1p1Green" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          height: 4,
          minHeight: "2rem",
          flex: 1,
          background: colors.background.tertiary,
          borderRadius: "4px",
          color: colorMap.Green,
          textAlign: "center",
          fontSize: "16px",
          border: `1px solid ${colors.border.secondary}`,
          padding: 1,
        },
      },
      {
        props: { variant: "amnShowFlex1br4br1p1Base" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          height: 4,
          minHeight: "2rem",
          flex: 1,
          background: colors.background.tertiary,
          borderRadius: "4px",
          color: colorMap.HardBlack,
          textAlign: "center",
          fontSize: "16px",
          border: `1px solid ${colors.border.secondary}`,
          padding: 1,
        },
      },
      {
        props: { variant: "amnShowFlex1br4br1p1Red" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          flex: 1,
          background: colors.background.tertiary,
          borderRadius: "4px",
          color: colorMap.Red,
          textAlign: "center",
          fontSize: "16px",
          border: `1px solid ${colors.border.secondary}`,
          padding: 1,
        },
      },

      // ═══════════════════════════════════════════════════════════
      // INDICATOR VARIANTS
      // ═══════════════════════════════════════════════════════════

      {
        props: { variant: "indicatorTopInfoOffsetCyan" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "14px",
          color: colors.status.info,
          position: "absolute",
          top: "-25px",
          right: "-15px",
        },
      },
      {
        props: { variant: "indicatorTopZeroRightInfoOffsetCyan" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "14px",
          color: colors.status.info,
          position: "absolute",
          top: "-25px",
          right: "0px",
        },
      },
      {
        props: { variant: "InfoOffsetCyan" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "12px",
          color: "#0891B2",
        },
      },
      {
        props: { variant: "InfoOffsetCyanOrange" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "12px",
          color: "#D97706",
        },
      },
      {
        props: { variant: "indicatorTopOffsetOrange" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "14px",
          color: colors.status.warning,
          position: "absolute",
          top: "-25px",
          right: "-15px",
        },
      },
      {
        props: { variant: "indicatorInfoTopRightBlue" as any },
        style: {
          fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
          fontWeight: "500",
          fontSize: "14px",
          color: colors.brand.primary,
          position: "absolute",
          top: "-25px",
          right: "-15px",
        },
      },

      // ═══════════════════════════════════════════════════════════
      // SCALED VARIANTS (Up/Down/Equal)
      // ═══════════════════════════════════════════════════════════

      ...[
        { size: "small", fontSize: "1.7em", scale: 0.85 },
        { size: "medium", fontSize: "1.1em", scale: 1.3 },
        { size: "large", fontSize: "0.8em", scale: 1.4 },
      ].flatMap(({ size, fontSize, scale }) =>
        ["Up", "Down", "Equal"].map((direction) => ({
          props: { variant: `${size}${direction}` as any },
          style: {
            fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Arial, sans-serif",
            fontSize,
            transform: `scale(${scale})`,
            color:
              direction === "Up"
                ? colors.status.success
                : direction === "Down"
                ? colors.status.error
                : colors.brand.primary,
            fontWeight: 500,
            display: "inline-block",
            transformOrigin: "right bottom",
            marginBottom: 1.2,
          },
        }))
      ),
    ],

    defaultProps: {
      // Preserve semantic heading tags for the hierarchy tiers (H1–H6) and
      // the deprecated display/title/subtitle aliases (an unmapped custom
      // variant falls back to <span> — see
      // MUI's Typography.js: `component || variantMapping[variant] ||
      // defaultVariantMapping[variant] || 'span'`). Passing `variantMapping`
      // via defaultProps REPLACES MUI's own map rather than merging with it,
      // so MUI's built-in entries are restated here explicitly — dropping
      // them would silently turn every still-native `variant="h5"` /
      // `variant="body1"` etc. across the app into a <span>. Only the
      // hierarchy-establishing tiers get a heading tag; display/body/button
      // /label tiers are left unmapped (falling back to <span>), matching
      // how MUI itself treats "caption"/"overline". Callers can still
      // override via the `component` prop as usual.
      variantMapping: {
        h1: "h1",
        h2: "h2",
        h3: "h3",
        h4: "h4",
        h5: "h5",
        h6: "h6",
        subtitle1: "h6",
        subtitle2: "h6",
        body1: "p",
        body2: "p",
        inherit: "p",

        display: "h1",
        displaySmall: "h2",
        displaySmallStrong: "h2",
        title: "h3",
        titleRegular: "h3",
        titleLight: "h3",
        titleStrong: "h3",
        subtitle: "h4",
        subtitleStrong: "h4",
        // Hierarchy tiers render as their heading tag (uppercase keys keep
        // them distinct from MUI's native lowercase h1-h6, whose ~300 call
        // sites are intentionally untouched). Display/Body/Button/Label
        // tiers are left unmapped (falling back to <span>), matching how
        // MUI itself treats "caption"/"overline".
        H1: "h1",
        H1Medium: "h1",
        H2: "h2",
        H2Medium: "h2",
        H3: "h3",
        H3Medium: "h3",
        H4: "h4",
        H4Medium: "h4",
        H5: "h5",
        H5Medium: "h5",
        H6: "h6",
        H6Medium: "h6",
      },
    },
  };
};
